import { addDays, isSameMonth, monthOf, shift, WEEKDAYS } from "../calendar/grid.js";
import { dayNumber, formatMonthOf, formatWeekdayDay } from "../format.js";

/** The existing API accepts at most 62 days; one calendar month fits in one read. */
export function availabilityMonthWindow(month: string, today: string) {
  const first = `${month.slice(0, 7)}-01`;
  return {
    startDate: first < today ? today : first,
    endDate: addDays(shift("month", first, 1), -1),
  };
}

export function AvailabilityCalendar({
  month,
  today,
  availableDays,
  selectedDay,
  loading,
  busy,
  onMonthChange,
  onDayChange,
}: {
  month: string;
  today: string;
  availableDays: ReadonlyMap<string, number>;
  selectedDay: string | null;
  loading: boolean;
  busy: boolean;
  onMonthChange: (month: string) => void;
  onDayChange: (day: string) => void;
}) {
  return (
    <section className="credit-booking-calendar" aria-label="Choose a day">
      <h3>Choose a day</h3>
      <div className="credit-calendar-navigation">
        <button
          type="button"
          className="action-tertiary"
          aria-label="Previous month"
          disabled={busy || loading || month.slice(0, 7) <= today.slice(0, 7)}
          onClick={() => onMonthChange(shift("month", month, -1))}
        >
          <span aria-hidden="true">←</span>
        </button>
        <strong aria-live="polite">{formatMonthOf(month)}</strong>
        <button
          type="button"
          className="action-tertiary"
          aria-label="Next month"
          disabled={busy || loading}
          onClick={() => onMonthChange(shift("month", month, 1))}
        >
          <span aria-hidden="true">→</span>
        </button>
      </div>
      <div className="credit-calendar-weekdays" aria-hidden="true">
        {WEEKDAYS.map((day) => <span key={day}>{day}</span>)}
      </div>
      <div className="credit-calendar-days" role="group" aria-label={`Dates in ${formatMonthOf(month)}`} aria-busy={loading}>
        {monthOf(month).map((day) => {
          if (!isSameMonth(day, month)) return <span key={day} aria-hidden="true" />;
          const count = availableDays.get(day) ?? 0;
          const available = day >= today && count > 0;
          return (
            <button
              key={day}
              type="button"
              className={`credit-calendar-day${available ? " has-times" : ""}`}
              aria-label={`${formatWeekdayDay(day)} · ${loading ? "checking availability" : available ? `${count} available time${count === 1 ? "" : "s"}` : "no available times"}`}
              aria-pressed={selectedDay === day}
              aria-current={day === today ? "date" : undefined}
              disabled={busy || loading || !available}
              onClick={() => onDayChange(day)}
            >
              {dayNumber(day)}
              <span className="credit-calendar-dot" aria-hidden="true" />
            </button>
          );
        })}
      </div>
      <p className="small muted credit-calendar-legend"><span className="credit-calendar-dot" aria-hidden="true" /> Times available · UK time</p>
    </section>
  );
}
